import { DashboardCard } from "./dashboard-card";
import { useAccountContext } from "../account-context";
import { formatCurrency, cn } from "@/lib/utils";
import { Spinner } from "@/components/spinner";
import { ChartContainer, type ChartConfig } from "@/components/ui/chart";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { PieChartIcon } from "lucide-react";
import { useMemo } from "react";

// Color palette for different markets
const COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "#8b5cf6",
  "#06b6d4",
  "#f59e0b",
];

const chartConfig: ChartConfig = {
  value: {
    label: "Position Value",
  },
};

export const PositionDistributionCard = () => {
  const { positions, isLoading } = useAccountContext();

  const chartData = useMemo(() => {
    if (!positions.length) return [];

    return positions.map((pos, index) => {
      // Frontend positions have size and currentPrice already parsed
      const size = Math.abs(pos.size);
      const markPrice = pos.currentPrice;
      const value = size * markPrice;

      return {
        name: pos.market,
        value,
        side: pos.side === "long" ? "Long" : "Short",
        size,
        color: COLORS[index % COLORS.length],
      };
    });
  }, [positions]);

  const totalValue = useMemo(
    () => chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData]
  );

  if (isLoading) {
    return (
      <DashboardCard title="Position Distribution" subtitle="Allocation by market">
        <div className="flex items-center justify-center h-64">
          <Spinner />
        </div>
      </DashboardCard>
    );
  }

  if (!chartData.length) {
    return (
      <DashboardCard title="Position Distribution" subtitle="Allocation by market" className="h-full">
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <PieChartIcon className="size-12 mb-3 opacity-50" />
          <p className="text-sm">No open positions</p>
          <p className="text-xs">Your position allocation will appear here</p>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard title="Position Distribution" subtitle="Allocation by market" className="h-full">
      <div className="flex flex-col h-full">
        {/* Chart */}
        <div className="h-44 relative">
          <ChartContainer config={chartConfig} className="h-full w-full">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} strokeWidth={0} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const data = payload[0].payload;
                  const percentage = ((data.value / totalValue) * 100).toFixed(1);
                  return (
                    <div className="bg-popover border border-border rounded-lg p-2 shadow-lg text-xs">
                      <div className="font-semibold">{data.name}</div>
                      <div className="text-muted-foreground">{data.side}</div>
                      <div className="mt-1">
                        <span className="font-medium">${formatCurrency(data.value, 2)}</span>
                        <span className="text-muted-foreground ml-1">({percentage}%)</span>
                      </div>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ChartContainer>
          {/* Center label */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className="text-xs text-muted-foreground">Total</div>
              <div className="text-lg font-bold">${formatCurrency(totalValue, 0)}</div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 space-y-2 flex-1 overflow-auto">
          {chartData.map((item) => {
            const percentage = ((item.value / totalValue) * 100).toFixed(1);
            return (
              <div
                key={item.name}
                className="flex items-center justify-between text-sm"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-sm"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="font-medium">{item.name}</span>
                  <span
                    className={cn(
                      "text-xs px-1.5 py-0.5 rounded",
                      item.side === "Long"
                        ? "bg-bid text-bid-foreground"
                        : "bg-ask text-ask-foreground"
                    )}
                  >
                    {item.side}
                  </span>
                </div>
                <div className="text-muted-foreground">{percentage}%</div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardCard>
  );
};
